/**
 * Image helpers for aquascape uploads. The expensive part — reading a
 * `File`, drawing it to a canvas, and re-encoding a downscaled JPEG so the
 * photo is small enough to persist to the Pi as a self-contained data URL —
 * lives here. `fitWithin` is pure and unit-tested; the canvas path is a thin
 * wrapper that the UI calls.
 */
export interface Size {
  readonly width: number;
  readonly height: number;
}

/** Scale (w,h) down to fit inside a `maxDim`×`maxDim` box, never up. */
export function fitWithin(w: number, h: number, maxDim: number): Size {
  if (w <= 0 || h <= 0) return { width: maxDim, height: maxDim };
  const scale = Math.min(1, maxDim / Math.max(w, h));
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(new Error('Could not read image file'));
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = src;
  });
}

/**
 * Read an uploaded image file and return a downscaled JPEG data URL so it
 * can be stored inline and saved to the Pi. Falls back to the original
 * data URL if a canvas context is unavailable.
 */
export async function fileToDownscaledDataUrl(
  file: File,
  maxDim = 2000,
  quality = 0.82,
): Promise<string> {
  const original = await readFileAsDataUrl(file);
  try {
    const img = await loadImage(original);
    const { width, height } = fitWithin(
      img.naturalWidth || img.width,
      img.naturalHeight || img.height,
      maxDim,
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    return original;
  }
}
